import { GRADES_API_BASE_URL, GRADES_TENANT_ID } from '../config/gradesApi';
import { getTenantHeaders } from '../config/tenant';

export interface GradeDto {
  id: string;
  name: string;
  description?: string | null;
  level: number;
  code: string;
  roleName: string;
  active: boolean;
  subjectCount?: number;
  sectionName?: string | null;
}

const normalizeGrade = (row: Record<string, unknown>): GradeDto | null => {
  const id = String(row.id ?? row.Id ?? '');
  const name = String(row.name ?? row.Name ?? '');
  const code = String(row.code ?? row.Code ?? '');
  if (!id || !name || !code) return null;
  return {
    id,
    name,
    description: (row.description ?? row.Description) as string | null | undefined,
    level: Number(row.level ?? row.Level ?? 0),
    code,
    roleName: String(row.roleName ?? row.RoleName ?? ''),
    active: Boolean(row.active ?? row.Active ?? true),
    subjectCount: Number(row.subjectCount ?? row.SubjectCount ?? 0),
    sectionName: (row.sectionName ?? row.SectionName) as string | null | undefined,
  };
};

type GradesFetchResult = {
  ok: boolean;
  grades: GradeDto[];
  message?: string;
  status: number;
};

const gradesHomeRequestCache = new Map<string, Promise<GradesFetchResult>>();

function gradesHomeCacheKey(isAuthenticated: boolean, studentId: string | null | undefined): string {
  return isAuthenticated && studentId ? `by-student:${studentId}` : 'all';
}

/**
 * Home page grades: GET api/Grade or GET api/Grade/by-student (deduped in-flight).
 */
export async function fetchGradesForHome(
  options: {
    isAuthenticated: boolean;
    studentId?: string | null;
    signal?: AbortSignal;
  },
  force = false
): Promise<GradesFetchResult> {
  const { isAuthenticated, studentId, signal } = options;
  const useStudentGrades = isAuthenticated && Boolean(studentId);
  const cacheKey = gradesHomeCacheKey(isAuthenticated, studentId);

  if (!force) {
    const pending = gradesHomeRequestCache.get(cacheKey);
    if (pending) return pending;
  }

  const request = (async (): Promise<GradesFetchResult> => {
    const url = useStudentGrades
      ? `${GRADES_API_BASE_URL}/Grade/by-student?studentId=${encodeURIComponent(studentId!)}`
      : `${GRADES_API_BASE_URL}/Grade`;

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Tenant-ID': GRADES_TENANT_ID,
      ...getTenantHeaders(),
    };
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
    if (useStudentGrades && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const res = await fetch(url, { method: 'GET', headers, signal });
      if (!res.ok) {
        return {
          ok: false,
          grades: [],
          status: res.status,
          message: `تعذر تحميل المراحل (${res.status})`,
        };
      }

      const json = (await res.json()) as Record<string, unknown>;
      const { ok, data, message } = readEnvelope(json);
      if (!ok || !data) {
        return {
          ok: false,
          grades: [],
          status: res.status,
          message: message || 'لم يتم إرجاع بيانات المراحل',
        };
      }

      const grades = data
        .map((row) => normalizeGrade(row as Record<string, unknown>))
        .filter((g): g is GradeDto => g !== null && g.active)
        .sort((a, b) => a.level - b.level);

      return { ok: true, grades, status: res.status };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      return {
        ok: false,
        grades: [],
        status: 0,
        message: 'حدث خطأ أثناء الاتصال بالخادم',
      };
    }
  })();

  gradesHomeRequestCache.set(cacheKey, request);
  try {
    return await request;
  } finally {
    if (gradesHomeRequestCache.get(cacheKey) === request) {
      gradesHomeRequestCache.delete(cacheKey);
    }
  }
}

/**
 * Backend: GET api/Grade (same endpoint as home GradeCards)
 */
export async function fetchGrades(): Promise<{
  ok: boolean;
  grades: GradeDto[];
  message?: string;
  status: number;
}> {
  try {
    const res = await fetch(`${GRADES_API_BASE_URL}/Grade`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Tenant-ID': GRADES_TENANT_ID,
      },
    });

    if (!res.ok) {
      return {
        ok: false,
        grades: [],
        status: res.status,
        message: `تعذر تحميل المراحل (${res.status})`,
      };
    }

    const json = (await res.json()) as Record<string, unknown>;
    const { ok, data, message } = readEnvelope(json);
    if (!ok || !data) {
      return {
        ok: false,
        grades: [],
        status: res.status,
        message: message || 'لم يتم إرجاع بيانات المراحل',
      };
    }

    const grades = data
      .map((row) => normalizeGrade(row as Record<string, unknown>))
      .filter((g): g is GradeDto => g !== null && g.active)
      .sort((a, b) => a.level - b.level);

    return { ok: true, grades, status: res.status };
  } catch {
    return {
      ok: false,
      grades: [],
      status: 0,
      message: 'حدث خطأ أثناء الاتصال بالخادم',
    };
  }
}

export interface SubjectDto {
  id: string;
  name: string;
  description?: string | null;
  code: string;
  active: boolean;
  gradeId: string;
  gradeName?: string | null;
  teacherCount: number;
  courseCount: number;
}

export interface SubjectCourseDto {
  id: string;
  courseName: string;
  term?: string | null;
  active: boolean;
  price: number;
  grade?: string | null;
  description?: string | null;
  imagePath?: string | null;
  modificationDate?: string;
  subjectId?: string;
  subjectName?: string | null;
  teacherName?: string | null;
}

const readEnvelope = (json: Record<string, unknown>) => {
  const ok = Boolean(json.success ?? json.Success);
  const raw = json.data ?? json.Data;
  const data = Array.isArray(raw) ? raw : null;
  const message = (json.message ?? json.Message) as string | undefined;
  return { ok, data, message };
};

const normalizeSubject = (row: Record<string, unknown>): SubjectDto | null => {
  const id = String(row.id ?? row.Id ?? '');
  const name = String(row.name ?? row.Name ?? '');
  const code = String(row.code ?? row.Code ?? '');
  if (!id || !name) return null;
  return {
    id,
    name,
    description: (row.description ?? row.Description) as string | null | undefined,
    code,
    active: Boolean(row.active ?? row.Active ?? true),
    gradeId: String(row.gradeId ?? row.GradeId ?? ''),
    gradeName: (row.gradeName ?? row.GradeName) as string | null | undefined,
    teacherCount: Number(row.teacherCount ?? row.TeacherCount ?? 0),
    courseCount: Number(row.courseCount ?? row.CourseCount ?? 0),
  };
};

const normalizeCourse = (row: Record<string, unknown>): SubjectCourseDto | null => {
  const id = String(row.id ?? row.Id ?? '');
  const courseName = String(row.courseName ?? row.CourseName ?? '');
  if (!id || !courseName) return null;
  return {
    id,
    courseName,
    term: (row.term ?? row.Term) as string | null | undefined,
    active: Boolean(row.active ?? row.Active ?? true),
    price: Number(row.price ?? row.Price ?? 0),
    grade: (row.grade ?? row.Grade) as string | null | undefined,
    description: (row.description ?? row.Description) as string | null | undefined,
    imagePath: (row.imagePath ?? row.ImagePath) as string | null | undefined,
    modificationDate: (row.modificationDate ?? row.ModificationDate) as string | undefined,
    subjectId: row.subjectId != null || row.SubjectId != null ? String(row.subjectId ?? row.SubjectId) : undefined,
    subjectName: (row.subjectName ?? row.SubjectName) as string | null | undefined,
    teacherName: (row.teacherName ?? row.TeacherName) as string | null | undefined,
  };
};

/**
 * Backend: GET api/Grade/{id}/subjects (GradeController.GetGradeSubjects)
 */
export async function fetchGradeSubjects(gradeId: string): Promise<{
  ok: boolean;
  subjects: SubjectDto[];
  message?: string;
  status: number;
}> {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;

  const res = await fetch(
    `${GRADES_API_BASE_URL}/Grade/${encodeURIComponent(gradeId)}/subjects`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Tenant-ID': GRADES_TENANT_ID,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (res.status === 401) {
    return {
      ok: false,
      subjects: [],
      status: 401,
      message: 'يجب تسجيل الدخول لعرض مواد هذه المرحلة.',
    };
  }

  if (!res.ok) {
    return {
      ok: false,
      subjects: [],
      status: res.status,
      message: `تعذر تحميل المواد (${res.status})`,
    };
  }

  const json = (await res.json()) as Record<string, unknown>;
  const { ok, data, message } = readEnvelope(json);
  if (!ok || !data) {
    return { ok: false, subjects: [], status: res.status, message: message || 'لم يتم إرجاع بيانات المواد' };
  }

  const subjects = data
    .map((row) => normalizeSubject(row as Record<string, unknown>))
    .filter((s): s is SubjectDto => s !== null && s.active);

  return { ok: true, subjects, status: res.status };
}

/**
 * Backend: GET api/Subject/{id}/courses (SubjectController.GetSubjectCourses)
 */
export async function fetchSubjectCourses(subjectId: string): Promise<{
  ok: boolean;
  courses: SubjectCourseDto[];
  message?: string;
  status: number;
}> {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;

  const res = await fetch(
    `${GRADES_API_BASE_URL}/Subject/${encodeURIComponent(subjectId)}/courses`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Tenant-ID': GRADES_TENANT_ID,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (!res.ok) {
    return {
      ok: false,
      courses: [],
      status: res.status,
      message: `تعذر تحميل المناهج (${res.status})`,
    };
  }

  const json = (await res.json()) as Record<string, unknown>;
  const { ok, data, message } = readEnvelope(json);
  if (!ok || !data) {
    return { ok: false, courses: [], status: res.status, message: message || 'لم يتم إرجاع بيانات المناهج' };
  }

  const courses = data
    .map((row) => normalizeCourse(row as Record<string, unknown>))
    .filter((c): c is SubjectCourseDto => c !== null && c.active);

  return { ok: true, courses, status: res.status };
}
